import {
	ArrowForward,
	ContentCopy,
	Error,
	Lock,
	Login,
	Merge,
	Public,
	SwapVerticalCircle,
} from "@mui/icons-material";
import { format } from "date-fns";
import { type ReactNode } from "react";

import { copyContent } from "~/components/common/CopyText";
import { SyncPlan } from "~/components/git/GitHistoryDialog/SyncPlan";
import { useDocumentVersioningDialog } from "~/components/git/GitHistoryDialog/useDocumentVersioningDialog";
import { useDoLogin } from "~/components/topbar/LoginButton";
import { getDocumentDirPath } from "~/data/filesystem";
import { useRouteParams } from "~/data/route";
import { useGitStatus } from "~/state";
import { Button, IconButton, LinkButton } from "~/ui/Button";
import { GitRefspec } from "~/ui/GitRefspec";
import { Label } from "~/ui/Label";

import { usePropertiesPane } from "../../easel/properties-pane/usePropertiesPane";
import { useSyncDocument } from "../../properties-pane/publish-tab/usePublish";

export const PublishTab = () => {
	const [doLogin, loginDisabled] = useDoLogin();
	const {
		isSyncing,

		canSync,
		sync,
		canUnpublish,
		unpublish,

		syncStatus,
		planStatus,
		syncStatusMessage,
		publishUrl,
	} = useSyncDocument({ mode: "publish-if-unpublished" });
	const propertiesPane = usePropertiesPane();

	const { workspaceName, documentId } = useRouteParams();
	const dialogState = useDocumentVersioningDialog({ workspaceName, documentId });

	const { data: gitStatus } = useGitStatus(documentId);
	const hasChanges = gitStatus && (gitStatus.insertions > 0 || gitStatus.deletions > 0);

	let header: ReactNode;
	if (planStatus === "unachievable-not-on-synced-branch") {
		header = (
			<div className="flex flex-col gap-4 text-center px-4 pt-4 -mb-2 rounded-t-md">
				<div className="[&_svg]:size-6">
					<Error className="text-palette-negative-9" />
				</div>
				<div className="text-base font-medium">Publish to web</div>
			</div>
		);
	} else if (planStatus === "unachievable-behind-remote") {
		header = (
			<div className="flex flex-col gap-4 text-center px-4 pt-4 -mb-2 rounded-t-md">
				<div className="[&_svg]:size-6">
					<Merge className="text-2xl text-tertiary" />
				</div>
				<div className="text-base font-medium">Publish to web</div>
			</div>
		);
	} else if (planStatus === "unachievable-unauthorized") {
		header = (
			<div className="flex flex-col gap-4 text-center px-4 pt-4 -mb-2 rounded-t-md">
				<div className="[&_svg]:size-6">
					<Lock className="text-2xl text-tertiary" />
				</div>
				<div className="text-base font-medium">Publish to web</div>
			</div>
		);
	} else if (planStatus === "unachievable-not-authenticated") {
		header = (
			<div className="flex flex-col gap-2 text-center px-4 pt-4 rounded-t-md">
				<div className="text-base font-medium">Publish to web</div>
				<div className="text-sm">You must be logged in to publish a document.</div>
			</div>
		);
	} else if (syncStatus !== "published") {
		header = (
			<div className="flex flex-col gap-2 text-center px-4 pt-4 rounded-t-md">
				<div className="text-base font-medium">Publish to web</div>
				<div className="text-xs">
					Creates a dedicated URL that will be updated with the contents of this doc any
					time you push to the <GitRefspec refspec="main" /> branch.
				</div>
			</div>
		);
	} else if (hasChanges) {
		header = (
			<div className="flex flex-col gap-2 text-center px-4 pt-4 rounded-t-md">
				<div className="text-base font-medium">Publish to web</div>
			</div>
		);
	} else {
		header = (
			<div className="flex flex-col gap-2 text-center px-4 pt-4 rounded-t-md">
				<div className="text-base font-medium">Publish to web</div>
				<div className="text-xs">
					The URL below will be updated any time you push to the{" "}
					<GitRefspec refspec="main" /> branch.
				</div>
			</div>
		);
	}

	let content: ReactNode;
	if (planStatus === "unachievable-not-on-synced-branch") {
		content = (
			<div className="text-center text-secondary">
				To modify publication settings, you must <br />
				switch to the publish branch, <GitRefspec refspec="main" />
			</div>
		);
	} else if (planStatus === "unachievable-behind-remote") {
		content = (
			<div className="flex flex-col gap-4">
				<div className="text-center text-secondary">
					Your branch is behind the remote branch.
					<br />
					Please use the git CLI to pull and <br />
					merge the latest changes.
				</div>
				<Button
					preset="secondary"
					size="sm"
					elevation="flat"
					onClick={async () => {
						const documentPath = await getDocumentDirPath(documentId);
						await copyContent(documentPath);
					}}
					startIcon={<ContentCopy />}
				>
					Copy path to document
				</Button>
			</div>
		);
	} else if (planStatus === "unachievable-unauthorized") {
		content = (
			<div className="text-center text-secondary">
				You do not have permissions
				<br />
				to publish this document.
			</div>
		);
	} else if (planStatus === "unachievable-not-authenticated") {
		content = (
			<Button
				className="grow"
				preset="primary"
				size="sm"
				elevation="flat"
				startIcon={<Login />}
				onClick={doLogin}
				disabled={loginDisabled}
			>
				Log in
			</Button>
		);
	} else if (syncStatus !== "published") {
		content = (
			<div className="grow flex flex-col gap-2">
				<Button
					className="bg-transparent hover:bg-inherit focus:bg-inherit active:bg-inherit"
					preset="secondary"
					size="sm"
					elevation="flat"
					disabled={!canSync}
					onClick={() => propertiesPane.togglePane({ pane: "publish" })}
					endIcon={<ArrowForward />}
				>
					Customize template, style, permissions
				</Button>
				<Button
					// This is ok because we are focus-trapping the popover, and
					// when we dismiss the popover, we restore the focus to the
					// previous state.
					//
					// eslint-disable-next-line jsx-a11y/no-autofocus
					autoFocus
					className="grow"
					preset="primary"
					size="sm"
					loading={isSyncing}
					// Icon lets us display both a loading spinner and a message, but only when
					// we're publishing.
					startIcon={isSyncing && syncStatusMessage !== null ? <Public /> : undefined}
					disabled={!canSync}
					onClick={() => sync(`Publishing changes ${format(new Date(), "yyyy/MM/dd")}`)}
				>
					{syncStatusMessage ?? "Publish the doc"}
				</Button>
				<SyncPlan mode="publish-if-unpublished" bg="raised" />
			</div>
		);
	} else if (hasChanges) {
		content = (
			<div className="grow flex flex-col gap-2 min-w-0">
				<div className="flex flex-col min-w-0 gap-1">
					<div className="flex items-center gap-2">
						<Label className="shrink-0">Published doc URL</Label>
						<div className="truncate text-secondary">{publishUrl}</div>
						<IconButton
							className="shrink-0"
							preset="quaternary"
							size="xs"
							icon={<ContentCopy />}
							label="Copy"
							onClick={() => copyContent(publishUrl)}
						/>
					</div>
				</div>

				<div className="flex gap-2">
					<Button
						className="bg-transparent hover:bg-inherit focus:bg-inherit active:bg-inherit"
						preset="secondary"
						size="sm"
						elevation="flat"
						loading={isSyncing}
						onClick={() => propertiesPane.togglePane({ pane: "publish" })}
						endIcon={<ArrowForward />}
					>
						Customize template, permissions
					</Button>

					<Button
						className="grow"
						preset="destructive"
						size="sm"
						variant="outlined-subtle"
						elevation="flat"
						loading={isSyncing}
						disabled={!canUnpublish}
						onClick={() => unpublish()}
					>
						Unpublish
					</Button>
				</div>

				<div className="flex flex-col gap-4 text-xs bg-palette-brand-2 border border-palette-brand-6 rounded-md p-4">
					<div className="flex gap-2 text-palette-brand-12">
						<SwapVerticalCircle className="text-palette-brand-9 mt-0.5" />{" "}
						<div className="text-sm">
							Local changes will not be published until you push them to the{" "}
							<GitRefspec refspec="main" /> branch.
						</div>
					</div>
					<Button
						// This is ok because we are focus-trapping the popover, and when we dismiss
						// the popover, we restore the focus to the previous state.
						//
						// eslint-disable-next-line jsx-a11y/no-autofocus
						autoFocus
						preset="primary"
						size="sm"
						elevation="flat"
						onClick={() => dialogState.setSelection("commit")}
					>
						View diff and publish changes
					</Button>
				</div>
			</div>
		);
	} else {
		content = (
			<div className="grow flex flex-col min-w-0 gap-2">
				<div className="flex flex-col min-w-0 gap-1">
					<div className="flex items-center gap-2">
						<Label className="shrink-0">Published doc URL</Label>
						<div className="truncate text-secondary">{publishUrl}</div>
						<IconButton
							className="shrink-0"
							preset="quaternary"
							size="xs"
							icon={<ContentCopy />}
							label="Copy"
							onClick={() => copyContent(publishUrl)}
						/>
					</div>
				</div>

				<Button
					className="bg-transparent hover:bg-inherit focus:bg-inherit active:bg-inherit"
					preset="secondary"
					size="sm"
					elevation="flat"
					onClick={() => propertiesPane.togglePane({ pane: "publish" })}
					endIcon={<ArrowForward />}
				>
					Customize template, style, permissions
				</Button>

				<div className="flex gap-2">
					<Button
						className="w-1/2"
						preset="destructive"
						size="sm"
						variant="outlined-subtle"
						elevation="flat"
						loading={isSyncing}
						disabled={!canUnpublish}
						onClick={() => unpublish()}
					>
						Unpublish
					</Button>
					<LinkButton
						className="w-1/2"
						preset="primary"
						size="sm"
						elevation="flat"
						href={publishUrl ?? "#"}
						loading={isSyncing}
						target="_blank"
						startIcon={isSyncing ? <Public /> : undefined}
					>
						{isSyncing ? "Provisioning URL..." : "View site"}
					</LinkButton>
				</div>
			</div>
		);
	}

	return (
		<>
			{header}
			<div className="flex px-4 pb-4 gap-2">{content}</div>
		</>
	);
};
